import React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  useTheme,
  useMediaQuery,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';

const ImprovedStepper = ({ 
  activeStep, 
  steps, 
  orientation = 'horizontal',
  showProgress = true,
  completedSteps = []
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Mantemos sempre a orientação horizontal para melhorar a experiência mobile
  const actualOrientation = orientation;

  const getStepIcon = (index) => {
    if (completedSteps.includes(index)) {
      return <CheckCircleIcon color="success" />;
    } else if (index === activeStep) {
      return <PlayArrowIcon color="primary" />;
    } else {
      return <RadioButtonUncheckedIcon color="disabled" />;
    }
  };

  const getStepStatus = (index) => {
    if (completedSteps.includes(index)) return 'completed';
    if (index === activeStep) return 'active';
    if (index < activeStep) return 'completed';
    return 'pending';
  };

  return (
    <Box sx={{ width: '100%', mb: isMobile ? 1 : 3 }}>
      {showProgress && (
        <Box sx={{ mb: isMobile ? 1 : 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
            Progresso: {activeStep + 1} de {steps.length}
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: isMobile ? 3 : 4,
              backgroundColor: theme.palette.grey[200],
              borderRadius: 2,
              mt: 0.5,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                width: `${((activeStep + 1) / steps.length) * 100}%`,
                height: '100%',
                backgroundColor: theme.palette.primary.main,
                transition: 'width 0.3s ease',
                borderRadius: 2
              }}
            />
          </Box>
        </Box>
      )}

      <Stepper 
        activeStep={activeStep} 
        orientation={actualOrientation}
        alternativeLabel={isMobile} // Coloca os labels abaixo dos ícones no mobile
        sx={{
          padding: isMobile ? '0px' : undefined,
          overflowX: isMobile ? 'auto' : undefined, // Permite rolar horizontalmente no mobile se necessário
          '& .MuiStep-root': {
            padding: isMobile ? '0 4px' : undefined,
          },
          '& .MuiStepConnector-line': {
            minHeight: isMobile ? '12px' : '24px'
          },
          '& .MuiStepLabel-root': {
            cursor: 'pointer',
            padding: isMobile ? '2px 0' : undefined,
            alignItems: isMobile ? 'center' : 'flex-start',
          },
          '& .MuiStepLabel-iconContainer': {
            paddingRight: isMobile ? 0 : undefined,
            '& svg': {
              fontSize: isMobile ? '1rem' : undefined
            }
          },
          '& .MuiStepLabel-label': {
            fontSize: { xs: '0.7rem', sm: '0.775rem', md: '0.875rem' },
            fontWeight: 500,
            lineHeight: isMobile ? 1.1 : undefined,
            textAlign: isMobile ? 'center' : undefined,
            marginTop: isMobile ? '4px' : undefined,
          },
          '& .MuiStepLabel-label.Mui-active': {
            color: theme.palette.primary.main,
            fontWeight: 600,
          },
          '& .MuiStepLabel-label.Mui-completed': {
            color: theme.palette.success.main,
          }
        }}
      >
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          
          return (
            <Step key={step.label || step} completed={status === 'completed'}>
              <StepLabel
                icon={getStepIcon(index)}
                optional={
                  step.optional && (
                    <Chip 
                      label="Opcional" 
                      size="small" 
                      variant="outlined" 
                      color="default"
                    />
                  )
                }
              >
                <Box>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: status === 'active' ? 600 : 400,
                      color: status === 'active' ? 'primary.main' : 
                             status === 'completed' ? 'success.main' : 'text.secondary'
                    }}
                  >
                    {step.label || step}
                  </Typography>
                  {step.description && (
                    <Typography variant="caption" color="text.secondary">
                      {step.description}
                    </Typography>
                  )}
                </Box>
              </StepLabel>
              
              {actualOrientation === 'vertical' && step.content && (
                <StepContent>
                  <Box sx={{ pb: 2 }}>
                    {step.content}
                  </Box>
                </StepContent>
              )}
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default ImprovedStepper;